import { GameMode } from '@nolimitcity/slot-game/bin/flowcontrol/GameMode';
import { gsap } from 'gsap';
import { Presentation, PresentationState } from '../../../../core/gameflow/controller/Presentation';
import { ReelState } from '../../../../core/reel/ReelEngineView';
import { GameSetting } from '../../../../core/setting/GameSetting';
import { ArrayHelper } from '../../../../core/utils/ArrayHelper';
import { Helper } from '../../../../core/utils/Helper';
import { Beheaded } from '../../../Beheaded';
import { SoundConfig } from '../../../config/sound/SoundConfig';
import { SoundManager } from '../../../config/sound/SoundManager';
import { BeheadedEngineController } from '../../../reels/BeheadedEngineController';
import { BeheadedEngineManager } from '../../../reels/BeheadedEngineManager';
import { GameData } from '../../../server/data/GameData';
import { SlotGame } from '@nolimitcity/slot-game/bin/SlotGame';
import { StateHandler } from '../../../../core/utils/StateHandler';
import { Logger } from '@nolimitcity/slot-launcher/bin/utils/Logger';

export class BonusState extends StateHandler {
    public static readonly SHOW_SYMBOLS: string = 'showSymbols';
    public static readonly UPGRADE: string = 'upgrade';
    public static readonly SHOWLOGO: string = 'showlogo';
    public static readonly DONE: string = 'done';
}

export class StopPresentation extends Presentation {
    protected gameData: GameData;
    protected animationTl: gsap.core.Timeline;
    protected scatterPos: number[];
    protected evaluatedArea: string[][];
    private currentLevel: number;
    private nextLevel: number;
    protected mode: string;
    protected nextMode: string;
    private wasFeatureBuy: boolean;
    protected isFSSUPER: boolean = false;
    private numberOfScatters: number = 0;

    private bonusIntroState: BonusState = new BonusState();
    private scatterSoundPlayed: boolean = false;

    protected get basicStopDelay(): number[] {
        let delay: number[] = [0, 0.05, 0.1, 0.15, 0.2];

        if (GameSetting.isFastSpin || BeheadedEngineManager.isQuickStop) {
            delay = ArrayHelper.initArrayWithValues(BeheadedEngineManager.AllReels.length, () => 0);
        }

        return delay;
    }

    setGamedData(data: GameData): void {
        this.gameData = data;
        this.isFSSUPER = this.gameData.mode === 'FREESPIN_SUPER';
        this.scatterPos = data.Reel.scatterPositions;
        this.evaluatedArea = data.evaluatedArea;
        this.currentLevel = data.currentLevel;
        this.nextLevel = data.nextLevel;
        this.mode = data.mode;
        this.nextMode = data.nextMode;
        this.wasFeatureBuy = data.wasFeatureBuy;

        Beheaded.backgroundCharacter.setGameData(data);
        BeheadedEngineManager.parseData(data.Reel);
    }

    preStart(): void {
        Logger.log('Stop Presentation: PreStart Started');
        this.animationTl = Helper.setTimeline(this.animationTl, new gsap.core.Timeline());

        if (GameSetting.isBonusBet) this.bonusIntroState.set(BonusState.SHOW_SYMBOLS);

        Logger.log('Stop Presentation: PreStart Ended');
    }

    // Add animation offset that couldn't be calculated.
    protected addAnimOffset(): void {
        if (this.animationTl.isActive()) {
            this.timeline.to(this, { duration: this.animationTl.duration() - this.animationTl.time() }, 0);
        }
    }

    // Add Start Offset.
    protected addStartReelOffset(): boolean {
        const remaining: number = BeheadedEngineManager.remainingStartEngineOffset();
        this.timeline.to(this, { duration: remaining }, 0);
        return !!remaining;
    }

    // Add animation offset that couldn't be calculated.
    protected addRemainingReelStop(): void {
        this.timeline.to(
            this,
            { duration: BeheadedEngineManager.lastSpinningTime || BeheadedEngineManager.remainingAnimatedReelTime },
            0
        );
    }

    protected createStopSpins(): void {
        BeheadedEngineManager.RAll((rc: BeheadedEngineController) =>
            rc.createStop({
                stops: () => this.onStops(rc),
                bounce: () => this.onBounce(rc),
                stopped: () => this.onStopped(rc),
            })
        );
    }

    protected readyToDefineStop(): boolean {
        if (!this.PresentationState.isActive) {
            this.addAnimOffset();
            this.addRemainingReelStop();
            return false;
        }

        if (this.addStartReelOffset()) return false;

        return true;
    }

    createPresentation(enableSkip: Function) {
        enableSkip(!BeheadedEngineManager.isQuickStop);

        this.setNumberOfLandedScatter();

        if (GameSetting.isBonusBet) {
            switch (this.bonusIntroState.value) {
                case BonusState.SHOW_SYMBOLS:
                    this.timeline.add(Beheaded.backgroundCharacter.shootUpSymbols(this.nextLevel));
                    this.bonusIntroState.set(BonusState.UPGRADE);
                    break;
                case BonusState.UPGRADE:
                    this.timeline.add(Beheaded.backgroundCharacter.jumpToIndicators(this.nextLevel));
                    this.bonusIntroState.set(BonusState.SHOWLOGO);
                    break;
                case BonusState.SHOWLOGO:
                    Beheaded.backgroundCharacter.animateBackgroundCharacterForFeatureBuy(false, 0.5);
                    // this.timeline.to(this,{duration:0.25});
                    this.bonusIntroState.set(BonusState.DONE);
                    break;
                default:
                    this.PresentationState.setInactive();
            }
        } else {
            if (this.readyToDefineStop()) {
                this.createStopSpins();
                const delay: number[] = this.defineDelays();
                BeheadedEngineManager.stopReels(delay);
                this.PresentationState.setInactive();
            }
        }
    }

    protected defineDelays(): number[] {
        return this.basicStopDelay;
    }

    protected onStops(rc: BeheadedEngineController): void {
        if (!rc.reelID) SoundManager.playSoundEffect(SoundConfig.REELS_IN);
        rc.setGraphAlpha(0, rc.stopDuration);
        if (!this.gameData.isAnAvalanche) {
            rc.AllSymbols.forEach((s) => s.onLanded());
        }
    }

    setNumberOfLandedScatter(): void {
        this.numberOfScatters = 0;

        let isFirstScatterPositionSet: boolean = false;
        this.evaluatedArea.forEach((r, ri) => {
            r.forEach((s, si) => {
                if (s === 'S') {
                    this.numberOfScatters++;
                    if (!isFirstScatterPositionSet) {
                        isFirstScatterPositionSet = true;
                    }
                }
            });
        });
    }

    playBounceSound(reelID: number): void {
        if (!BeheadedEngineManager.isQuickStop)
            SoundManager.playReelStop(reelID, undefined, this.gameData.mode != GameMode.NORMAL);

        if (BeheadedEngineManager.lastStopOrder === reelID) {
            if (BeheadedEngineManager.isQuickStop)
                SoundManager.playReelStop(2, undefined, this.gameData.mode != GameMode.NORMAL);
        }
    }

    protected onBounce(rc: BeheadedEngineController): void {
        this.playBounceSound(rc.reelID);

        if (this.mode !== 'SUPER_FREESPIN') {
            const isScatter = rc.AllSymbols.filter((s) => s.symbolConfig.NAME === 'S');
            if (isScatter.length > 0 && !this.scatterSoundPlayed) {
                if (this.numberOfScatters === 1) {
                    Beheaded.soundPlayer.playSound(SoundConfig.SCATTER_LAND_1);
                } else if (this.numberOfScatters === 2) {
                    Beheaded.soundPlayer.playSound(SoundConfig.SCATTER_LAND_2);
                } else if (this.numberOfScatters > 2) {
                    Beheaded.soundPlayer.playSound(SoundConfig.SCATTER_LAND_3);
                }

                this.scatterSoundPlayed = true;
            }
        }
    }

    protected onStopped(rc: BeheadedEngineController): void {}

    skip(): void {
        this.PresentationState.set(PresentationState.ACTIVATED);
        BeheadedEngineManager.isQuickStop = true;
        super.skip();
    }

    isFinished(): boolean {
        Logger.log('Stop Presentation: Finish Started');
        if (GameSetting.isBonusBet) return true;

        const notFinished: boolean = !(
            BeheadedEngineManager.getFirstStoppingReel() >= 0 || BeheadedEngineManager.findReelWithState(ReelState.STEP) >= 0
        );

        if (!notFinished) return notFinished;

        if (this.animationTl.isActive()) return false;
        Logger.log('Stop Presentation: Finish Ended');
        return true;
    }

    done() {
        if (this.numberOfScatters > 2 && !this.mode.includes('SUPER_FREESPIN') && !this.nextMode.includes('AVALANCHE')) {
            if (this.nextMode.includes('SUPER_FREESPIN')) {
                // Beheaded.soundPlayer.playSound(SoundConfig.SCATTER_LAND_FALL);
            } else {
                Beheaded.soundPlayer.playSound(SoundConfig.SCATTER_LAND_FAIL);
            }
        }

        Logger.log('Stop Presentation: Done Ended');
        //reset progress bar of indicator if avalanche/fs are done

        if (Beheaded.backgroundCharacter.isLevelSoundActive && this.nextLevel === 0 && this.currentLevel !== 0) {
            Beheaded.backgroundCharacter.playLevelUpSounds(0, false, false, false, this.currentLevel);
        }

        if (this.nextMode === GameMode.NORMAL) {
            SlotGame.keypadPlugin.setZeroBetSpinCounter(-1);
        }

        // The symbols should be charged After the upgrade
        this.evaluatedArea &&
            this.evaluatedArea.forEach((reel, reelIndex) => {
                reel.forEach((sym, symIndex) => {
                    if (sym.includes('H')) {
                        const hSym = BeheadedEngineManager.getReel(reelIndex).getSymbol(symIndex);
                        hSym && hSym.chargeSymbol(), 0;
                    }
                });
            });

        this.scatterSoundPlayed = false;
    }
}
